import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Typography,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Tooltip } from '@eposnow/ui-core';
import { FormikContextType } from 'formik';
import { setFieldOnSessionStorage } from '../../helpers/helpers';
import FormikCheckbox from './FormikCheckbox';

export type CheckboxOption = {
  field: string;
  label: string;
  tooltip?: string;
};
type FormikCheckboxGroupAccordionProps = {
  formik: FormikContextType<unknown>;
  formName?: string;
  objectId?: string;
  field: string;
  label: string;
  tooltip?: string;
  checkboxList: CheckboxOption[];
};

export const handleAccordionCheckboxGroupChange =
  (
    formikObject: FormikContextType<unknown>,
    formName: string,
    objectId: string,
    checkboxOptions: CheckboxOption[]
  ) =>
  (event: React.ChangeEvent<HTMLInputElement>) => {
    checkboxOptions.forEach((option) => {
      formikObject.setFieldValue(option.field, event.target.checked);
      setFieldOnSessionStorage(formName, objectId, option.field, event.target.checked);
    });
  };

export const getCheckState = (
  formik: FormikContextType<unknown>,
  checkboxOptions: CheckboxOption[]
) => {
  const trueCount = checkboxOptions.filter((option) => formik.values[option.field] === true).length;
  if (trueCount === checkboxOptions.length) return 'all';
  if (trueCount === 0) return 'none';
  return 'some';
};

const FormikCheckboxGroupAccordion = ({
  formik,
  formName,
  objectId,
  field,
  label,
  tooltip,
  checkboxList,
}: FormikCheckboxGroupAccordionProps) => {
  const theme = useTheme();
  const [accordionExpanded, setAccordionExpanded] = useState(false);

  const checkboxState = useMemo(() => getCheckState(formik, checkboxList), [formik, checkboxList]);

  return (
    <Accordion
      expanded={accordionExpanded}
      onChange={(_event, isExpanded) => setAccordionExpanded(isExpanded)}
      sx={{
        boxShadow: 0,
        padding: 0,
        margin: 0,
        '&.MuiAccordion-root.Mui-expanded': {
          margin: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${field}-panel`}
        sx={{
          marginLeft: '-0.5rem',
          padding: 0,
          minHeight: '42px',
          '&.MuiAccordionSummary-root.Mui-expanded': {
            minHeight: '42px',
          },
          '& .MuiAccordionSummary-content.MuiAccordionSummary-contentGutters, & .MuiAccordionSummary-expandIconWrapper':
            {
              margin: 0,
            },
        }}
        onClick={(event) => {
          event.stopPropagation();
          setAccordionExpanded(!accordionExpanded);
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginRight: '0.5rem' }}>
            <Checkbox
              name={`${field}-checkbox`}
              onClick={(event) => event.stopPropagation()}
              onChange={handleAccordionCheckboxGroupChange(
                formik,
                formName,
                objectId,
                checkboxList
              )}
              indeterminate={checkboxState === 'some'}
              checked={checkboxState === 'all'}
            />
            <Typography>{label}</Typography>
          </Box>
          {tooltip && (
            <Tooltip title={tooltip} theme={theme}>
              <InfoOutlinedIcon fontSize="small" color="action" />
            </Tooltip>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails
        sx={{
          paddingY: '0.5rem',
          paddingX: 0,
        }}
      >
        <Box
          sx={{
            border: '1px solid',
            borderColor: (currTheme) =>
              currTheme.palette.mode === 'light'
                ? currTheme?.palette?.grey?.['12p']
                : currTheme?.palette?.grey?.['23p'],
            borderRadius: '8px',
            padding: '0.5rem',
          }}
        >
          {checkboxList.map((checkbox) => (
            <Box key={checkbox.field}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormikCheckbox
                  label={checkbox.label}
                  id={checkbox.field}
                  name={checkbox.field}
                  type="checkbox"
                  formName={formName}
                  objectId={objectId}
                />
                {checkbox.tooltip && (
                  <Tooltip title={checkbox.tooltip} theme={theme}>
                    <InfoOutlinedIcon fontSize="small" color="action" />
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default FormikCheckboxGroupAccordion;
